export const ORPHAN_GAP_NEAR_POLICY_CONTRACT_VERSION = 1;
export const ORPHAN_GAP_NEAR_HORIZON_DAYS = 7;
export const ORPHAN_GAP_NEAR_MAX_DISCOUNT_PCT = 20;
export const ORPHAN_GAP_NEAR_MAX_DISTRIBUTION_COST_PCT = 95;

const LOW_CONFIDENCE_ACTIONS = new Set(["hold", "require_approval"]);

function normalizePositiveInteger(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizePercent(value, fallback) {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? String(Number((numeric * 100).toFixed(4)))
    : String(fallback);
}

function normalizeNullableAmount(value) {
  const numeric = Number(value);
  return value != null && value !== "" && Number.isFinite(numeric)
    ? String(Number(numeric.toFixed(2)))
    : "";
}

function normalizeCurrency(value) {
  return /^[A-Z]{3}$/.test(String(value || "").trim().toUpperCase())
    ? String(value).trim().toUpperCase()
    : "";
}

export function normalizeOrphanGapNearPolicyDraft(
  runtime = {},
  strategies = [],
  { defaultCurrencyByStrategyId = new Map() } = {}
) {
  const runtimeItems = Array.isArray(runtime?.items) ? runtime.items : [];
  const itemByStrategyId = new Map(
    runtimeItems
      .map((item) => [normalizePositiveInteger(item?.strategy_setting_id), item])
      .filter(([strategyId]) => strategyId != null)
  );
  const normalizedStrategies = (Array.isArray(strategies) ? strategies : [])
    .map((strategy) => ({
      id: normalizePositiveInteger(strategy?.id),
      scope_type: String(strategy?.scope_type || "hotel"),
      scope_id: strategy?.scope_id ?? null,
      strategy_mode: String(strategy?.strategy_mode || "balanced"),
      is_active: strategy?.is_active === true,
    }))
    .filter((strategy) => strategy.id != null)
    .sort((left, right) => left.id - right.id);
  const rateProductAssignments = Array.isArray(
    runtime?.rate_product_currency_assignments
  )
    ? runtime.rate_product_currency_assignments
    : [];
  const categoryOverrideIds = new Set(
    normalizedStrategies
      .filter(
        (strategy) =>
          strategy.is_active && strategy.scope_type === "room_category"
      )
      .map((strategy) => Number(strategy.scope_id))
  );
  const inferredCurrencyByStrategyId = new Map();
  normalizedStrategies.forEach((strategy) => {
    const currencies = new Set(
      rateProductAssignments
        .filter((assignment) => {
          if (
            assignment?.is_active !== true ||
            assignment?.master_status !== "active"
          ) {
            return false;
          }
          const categoryId = Number(assignment.room_category_id);
          if (strategy.scope_type === "room_category") {
            return categoryId === Number(strategy.scope_id);
          }
          return (
            strategy.scope_type === "hotel" &&
            strategy.scope_id == null &&
            !categoryOverrideIds.has(categoryId)
          );
        })
        .map((assignment) => normalizeCurrency(assignment.master_currency))
        .filter(Boolean)
    );
    if (currencies.size === 1) {
      inferredCurrencyByStrategyId.set(strategy.id, [...currencies][0]);
    }
  });

  return {
    contract_version: Number(runtime?.contract_version || 0),
    business_unit_id: normalizePositiveInteger(runtime?.business_unit_id),
    fixed_near_horizon_days: Number(
      runtime?.fixed_near_horizon_days || ORPHAN_GAP_NEAR_HORIZON_DAYS
    ),
    expected_configuration_hash:
      typeof runtime?.configuration_hash === "string" && runtime.configuration_hash.trim()
        ? runtime.configuration_hash.trim()
        : null,
    currency_assignments_available: Array.isArray(
      runtime?.rate_product_currency_assignments
    ),
    items: normalizedStrategies.map((strategy) => {
      const source = itemByStrategyId.get(strategy.id) || {};
      const isEnabled = source?.is_enabled === true;
      const defaultCurrency =
        defaultCurrencyByStrategyId?.get?.(strategy.id) ||
        inferredCurrencyByStrategyId.get(strategy.id);
      const lowConfidenceAction = LOW_CONFIDENCE_ACTIONS.has(
        String(source?.low_confidence_action || "")
      )
        ? String(source.low_confidence_action)
        : "require_approval";
      return {
        strategy_setting_id: strategy.id,
        // UI-only provenance for category > hotel inheritance. An explicit
        // disabled category row must not be mistaken for an absent override.
        policy_is_configured: itemByStrategyId.has(strategy.id),
        scope_type: strategy.scope_type,
        scope_id: strategy.scope_id,
        strategy_mode: strategy.strategy_mode,
        strategy_is_active: strategy.is_active,
        is_enabled: isEnabled,
        relax_min_los_enabled: source?.relax_min_los_enabled === true,
        min_los_floor_nights: String([1, 2].includes(Number(source?.min_los_floor_nights))
          ? Number(source.min_los_floor_nights)
          : 1),
        max_discount_pct: normalizePercent(
          source?.max_discount_pct,
          isEnabled ? 5 : 0
        ),
        min_adjacent_occupancy_pct: normalizePercent(
          source?.min_adjacent_occupancy_pct,
          85
        ),
        currency_code:
          normalizeCurrency(source?.currency_code) || normalizeCurrency(defaultCurrency),
        expected_currency_code:
          inferredCurrencyByStrategyId.get(strategy.id) || null,
        variable_cost_per_occupied_room: normalizeNullableAmount(
          source?.variable_cost_per_occupied_room
        ),
        distribution_cost_pct:
          source?.distribution_cost_pct == null || source.distribution_cost_pct === ""
            ? ""
            : normalizePercent(source.distribution_cost_pct, ""),
        weak_pace_required: true,
        economic_clearance_required: true,
        low_confidence_action: lowConfidenceAction,
      };
    }),
  };
}

export function updateOrphanGapNearPolicyDraftItem(
  draft,
  strategySettingId,
  field,
  value
) {
  const strategyId = normalizePositiveInteger(strategySettingId);
  if (strategyId == null) return draft;
  const booleanFields = new Set(["is_enabled", "relax_min_los_enabled"]);
  const textFields = new Set([
    "min_los_floor_nights",
    "max_discount_pct",
    "min_adjacent_occupancy_pct",
    "currency_code",
    "variable_cost_per_occupied_room",
    "distribution_cost_pct",
    "low_confidence_action",
  ]);
  if (
    field === "weak_pace_required" ||
    field === "economic_clearance_required" ||
    (!booleanFields.has(field) && !textFields.has(field))
  ) {
    return draft;
  }

  return {
    ...(draft || {}),
    items: (Array.isArray(draft?.items) ? draft.items : []).map((item) => {
      if (Number(item.strategy_setting_id) !== strategyId) return item;
      if (field === "is_enabled") {
        if (value !== true) {
          return {
            ...item,
            is_enabled: false,
            relax_min_los_enabled: false,
            max_discount_pct: "0",
            currency_code: "",
            variable_cost_per_occupied_room: "",
            distribution_cost_pct: "",
          };
        }
        return {
          ...item,
          is_enabled: true,
          relax_min_los_enabled: true,
          max_discount_pct:
            Number(item.max_discount_pct) > 0 ? item.max_discount_pct : "5",
        };
      }
      if (booleanFields.has(field)) return { ...item, [field]: value === true };
      if (field === "currency_code") {
        return { ...item, currency_code: String(value || "").toUpperCase() };
      }
      return { ...item, [field]: String(value ?? "") };
    }),
  };
}

export function validateOrphanGapNearPolicyDraft(draft, horizonPolicyDraft = null) {
  const errors = [];
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const seen = new Set();
  const horizonItems = (
    Array.isArray(horizonPolicyDraft?.items) ? horizonPolicyDraft.items : []
  ).filter((item) => item?.policy_is_configured !== false);
  const hasScopedHorizonItems = horizonItems.some(
    (item) => item?.scope_type === "hotel" || item?.scope_type === "room_category"
  );

  const resolveHorizonPolicy = (nearItem, strategyId) => {
    if (!hasScopedHorizonItems) {
      return horizonItems.find(
        (item) => Number(item?.strategy_setting_id) === strategyId
      );
    }
    const categoryMatches =
      nearItem?.scope_type === "room_category"
        ? horizonItems.filter(
            (item) =>
              item?.scope_type === "room_category" &&
              Number(item?.scope_id) === Number(nearItem?.scope_id)
          )
        : [];
    const hotelMatches = horizonItems.filter(
      (item) => item?.scope_type === "hotel" && item?.scope_id == null
    );
    return (categoryMatches.length ? categoryMatches : hotelMatches)[0];
  };

  items.forEach((item) => {
    const strategyId = normalizePositiveInteger(item?.strategy_setting_id);
    if (strategyId == null || seen.has(strategyId)) {
      errors.push("Each near-gap strategy must appear exactly once.");
    } else {
      seen.add(strategyId);
    }
    if (item?.weak_pace_required !== true || item?.economic_clearance_required !== true) {
      errors.push("Weak pace and economic clearance must remain mandatory.");
    }
    if (![1, 2].includes(Number(item?.min_los_floor_nights))) {
      errors.push("The MinLOS floor must be one or two nights.");
    }
    const maxDiscount = Number(item?.max_discount_pct);
    if (!Number.isFinite(maxDiscount) || maxDiscount < 0 || maxDiscount > 20) {
      errors.push("The near-gap discount cap must stay between 0 and 20%.");
    }
    const adjacentOccupancy = Number(item?.min_adjacent_occupancy_pct);
    if (
      !Number.isFinite(adjacentOccupancy) ||
      adjacentOccupancy < 0 ||
      adjacentOccupancy > 100
    ) {
      errors.push("Adjacent occupancy must stay between 0 and 100%.");
    }
    const currency = String(item?.currency_code || "").trim().toUpperCase();
    const variableCostText = String(item?.variable_cost_per_occupied_room ?? "").trim();
    const distributionText = String(item?.distribution_cost_pct ?? "").trim();
    const variableCost = Number(variableCostText);
    const distributionCost = Number(distributionText);
    const currencyPresent = currency !== "";
    const variableCostPresent = variableCostText !== "";
    const distributionPresent = distributionText !== "";
    const economicsPresent = [currencyPresent, variableCostPresent, distributionPresent];
    if (currencyPresent && !/^[A-Z]{3}$/.test(currency)) {
      errors.push("Economic evidence requires a three-letter currency code.");
    }
    if (variableCostPresent && (!Number.isFinite(variableCost) || variableCost < 0)) {
      errors.push("Variable occupied-room cost must be a non-negative amount.");
    }
    if (
      distributionPresent &&
      (!Number.isFinite(distributionCost) || distributionCost < 0 || distributionCost > 95)
    ) {
      errors.push("Distribution cost must stay between 0 and 95%.");
    }
    if (economicsPresent.some(Boolean) && !economicsPresent.every(Boolean)) {
      errors.push("Currency, variable cost and distribution cost must be complete together.");
    }
    if (!LOW_CONFIDENCE_ACTIONS.has(String(item?.low_confidence_action || ""))) {
      errors.push("Choose hold or require approval for low confidence.");
    }

    if (item?.is_enabled === true) {
      if (item?.relax_min_los_enabled !== true || !(maxDiscount > 0)) {
        errors.push("An enabled near-gap policy must relax MinLOS and allow a positive capped discount.");
      }
      if (!economicsPresent.every(Boolean)) {
        errors.push("An enabled near-gap policy requires complete economic evidence.");
      }
      if (
        draft?.currency_assignments_available === true &&
        !item?.expected_currency_code
      ) {
        errors.push("The enabled strategy must resolve to one master-rate currency.");
      }
      if (
        item?.expected_currency_code &&
        currency !== item.expected_currency_code
      ) {
        errors.push("Economic evidence currency must match the master room rate.");
      }
      const horizonPolicy = resolveHorizonPolicy(item, strategyId);
      if (horizonPolicyDraft && horizonPolicy?.is_enabled !== true) {
        errors.push("Enable the matching T07 orphan-gap policy first.");
      }
      if (
        horizonPolicy?.gap_1_enabled === true &&
        Number(item?.min_los_floor_nights) > 1
      ) {
        errors.push("A T07 one-night gap requires a one-night MinLOS floor.");
      }
    } else if (item?.relax_min_los_enabled === true || maxDiscount !== 0) {
      errors.push("A disabled near-gap policy cannot relax MinLOS or retain a discount cap.");
    }
  });

  return [...new Set(errors)];
}

function percentToRatio(value) {
  return Number((Number(value) / 100).toFixed(6));
}

export function buildOrphanGapNearPolicyPayload(draft) {
  return {
    expected_configuration_hash: draft?.expected_configuration_hash || null,
    items: (Array.isArray(draft?.items) ? draft.items : []).map((item) => ({
      strategy_setting_id: Number(item.strategy_setting_id),
      is_enabled: item.is_enabled === true,
      relax_min_los_enabled: item.relax_min_los_enabled === true,
      min_los_floor_nights: Number(item.min_los_floor_nights),
      max_discount_pct: percentToRatio(item.max_discount_pct),
      min_adjacent_occupancy_pct: percentToRatio(
        item.min_adjacent_occupancy_pct
      ),
      currency_code: String(item.currency_code || "").trim().toUpperCase() || null,
      variable_cost_per_occupied_room:
        String(item.variable_cost_per_occupied_room ?? "").trim() === ""
          ? null
          : Number(item.variable_cost_per_occupied_room),
      distribution_cost_pct:
        String(item.distribution_cost_pct ?? "").trim() === ""
          ? null
          : percentToRatio(item.distribution_cost_pct),
      weak_pace_required: true,
      economic_clearance_required: true,
      low_confidence_action: item.low_confidence_action,
    })),
  };
}
